import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, extractErr } from "@/lib/api";
import { useLocations } from "@/lib/locations";
import { EmptyState, Skeleton } from "@/components/EmptyState";
import { VirtualInventoryList } from "@/components/VirtualInventoryList";
import { Plus, Search, Pencil, Trash2, Upload, X, PackageSearch, AlertTriangle, Image as ImageIcon, History, MapPin, Zap, Truck, Bell, Mail } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { ItemDetailSheet } from "@/components/ItemDetailSheet";

const CATEGORIES = ["Raw Materials", "Packaging Materials", "Finished Goods", "Consumables"];

export default function Inventory() {
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlFilter = urlParams.get("filter");
  const locCtx = useLocations() || {};
  const { activeId, locationParams, locations = [] } = locCtx;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [showLowOnly, setShowLowOnly] = useState(urlFilter === "low");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...locationParams };
      if (search) params.search = search;
      if (category && category !== "all") params.category = category;
      if (showLowOnly) params.low_stock = true;
      const { data } = await api.get("/inventory", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(extractErr(e, "Failed to load inventory"));
    } finally {
      setLoading(false);
    }
  }, [search, category, showLowOnly, locationParams]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setOpen(true); };

  const onSave = async (form) => {
    try {
      if (editing) {
        await api.put(`/inventory/${editing.id}`, form);
        toast.success("Item updated");
      } else {
        await api.post("/inventory", form);
        toast.success("Item created");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(extractErr(e, "Save failed"));
    }
  };

  const onDelete = async (item) => {
    try {
      await api.delete(`/inventory/${item.id}`);
      toast.success("Item deleted");
      load();
    } catch (e) {
      toast.error(extractErr(e, "Delete failed"));
    }
  };

  return (
    <AppShell
      title="Inventory"
      subtitle={`${items.length} item${items.length === 1 ? "" : "s"}${showLowOnly ? " (low stock only)" : ""}`}
      headerActions={(
        <Button size="sm" onClick={openCreate} data-testid="inventory-create-item-button">
          <Plus className="w-4 h-4 mr-1.5" /> Add item
        </Button>
      )}
    >
      <div className="rounded-2xl border border-border bg-card">
        <div className="p-3 flex flex-wrap items-center gap-2 border-b border-border">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search name, SKU or barcode…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="inventory-filter-input"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[180px]" data-testid="inventory-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant={showLowOnly ? "default" : "ghost"}
            size="sm"
            onClick={() => {
              setShowLowOnly((v) => !v);
              if (urlFilter) navigate("/app/inventory", { replace: true });
            }}
            data-testid="inventory-low-stock-toggle"
          >
            <AlertTriangle className="w-4 h-4 mr-1.5" /> Low stock {showLowOnly ? "✓" : ""}
          </Button>
        </div>

        {loading && items.length === 0 ? (
          <div className="p-6 space-y-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={PackageSearch}
            title="No inventory items yet"
            description="Add your first item to start tracking stock. You can upload an image, set SKU/barcode, minimum stock levels, and reorder quantities."
            action={<Button onClick={openCreate} data-testid="inventory-empty-create"><Plus className="w-4 h-4 mr-1.5" /> Add first item</Button>}
            className="border-0"
          />
        ) : items.length > 150 ? (
          <div className="relative">
            <div className="flex items-center gap-2 px-4 py-1.5 text-[11px] text-muted-foreground border-b border-border bg-muted/30" data-testid="inventory-virtualized-notice">
              <Zap className="w-3 h-3 text-primary" /> Virtualized rendering enabled for {items.length} items — only visible rows are drawn for smooth scrolling.
            </div>
            <VirtualInventoryList items={items} onEdit={openEdit} onDelete={onDelete} onHistory={setHistoryFor} onSelect={setDetailFor} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="inventory-table">
              <thead className="sticky top-0 bg-card/95 backdrop-blur border-b border-border">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 w-12"></th>
                  <th className="px-4 py-2">SKU</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">Min</th>
                  <th className="px-4 py-2">Location</th>
                  <th className="px-4 py-2">Supplier</th>
                  <th className="px-4 py-2 w-28"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const low = it.min_stock > 0 && it.quantity <= it.min_stock;
                  return (
                    <tr key={it.id} className="hover:bg-accent/40 border-b border-border last:border-0" data-testid={`inventory-row-${it.id}`}>
                      <td className="px-4 py-2">
                        {it.image_base64 ? (
                          <img src={it.image_base64} alt={it.name} className="w-8 h-8 rounded object-cover border border-border" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-muted text-muted-foreground flex items-center justify-center">
                            <ImageIcon className="w-3.5 h-3.5" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">
                        <button
                          type="button"
                          className="text-left hover:text-primary hover:underline focus:text-primary focus:underline outline-none"
                          onClick={() => setDetailFor(it)}
                          data-testid={`inventory-sku-${it.id}`}
                        >
                          {it.sku}
                        </button>
                      </td>
                      <td className="px-4 py-2 font-medium">
                        <button
                          type="button"
                          className="text-left hover:text-primary hover:underline focus:text-primary focus:underline outline-none"
                          onClick={() => setDetailFor(it)}
                          data-testid={`inventory-name-${it.id}`}
                        >
                          {it.name}
                        </button>
                      </td>
                      <td className="px-4 py-2">
                        <span className="inline-flex rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[11px]">{it.category}</span>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        <span className={low ? "text-amber-600 dark:text-amber-400 font-medium" : ""}>
                          {it.quantity} {it.unit}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{it.min_stock || "—"}</td>
                      <td className="px-4 py-2 text-muted-foreground">{it.location || "—"}</td>
                      <td className="px-4 py-2 text-muted-foreground">{it.supplier || "—"}</td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setHistoryFor(it)} title="History" data-testid={`inventory-history-${it.id}`}>
                            <History className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(it)} title="Edit" data-testid={`inventory-edit-${it.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Delete" data-testid={`inventory-delete-${it.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete item?</AlertDialogTitle>
                                <AlertDialogDescription>This will remove “{it.name}”. Stock movement history remains.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => onDelete(it)}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ItemForm
        open={open}
        onClose={() => { setOpen(false); setEditing(null); }}
        onSave={onSave}
        initial={editing}
        locations={locations}
        defaultLocationId={activeId !== "all" ? activeId : null}
      />

      {historyFor && <HistoryModal item={historyFor} onClose={() => setHistoryFor(null)} />}
      {detailFor && (
        <ItemDetailSheet
          item={detailFor}
          onClose={() => setDetailFor(null)}
          onEdit={(it) => { setDetailFor(null); openEdit(it); }}
          onOpenHistory={(it) => { setDetailFor(null); setHistoryFor(it); }}
          locations={locations}
        />
      )}
    </AppShell>
  );
}

function ItemForm({ open, onClose, onSave, initial, locations = [], defaultLocationId = null }) {
  const [form, setForm] = useState({
    name: "", sku: "", barcode: "", category: "Raw Materials",
    quantity: 0, unit: "pcs", location: "", location_id: defaultLocationId || "", warehouse: "",
    supplier: "", supplier_id: "", min_stock: 0, reorder_qty: 0, unit_cost: 0, notes: "", image_base64: null,
  });
  const [suppliers, setSuppliers] = useState([]);
  const [supplierMode, setSupplierMode] = useState("select"); // "select" | "new"
  const [newSupplier, setNewSupplier] = useState({ name: "", email: "", phone: "", contact_person: "" });
  const [creatingSup, setCreatingSup] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load suppliers when dialog opens
  useEffect(() => {
    if (!open) return;
    api.get("/suppliers").then(({ data }) => setSuppliers(data.suppliers || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (initial) setForm({
      name: initial.name || "", sku: initial.sku || "", barcode: initial.barcode || "",
      category: initial.category || "Raw Materials", quantity: initial.quantity || 0,
      unit: initial.unit || "pcs", location: initial.location || "",
      location_id: initial.location_id || defaultLocationId || "",
      warehouse: initial.warehouse || "",
      supplier: initial.supplier || "",
      supplier_id: initial.supplier_id || "",
      min_stock: initial.min_stock || 0,
      reorder_qty: initial.reorder_qty || 0, unit_cost: initial.unit_cost || 0,
      notes: initial.notes || "", image_base64: initial.image_base64 || null,
    });
    else setForm({
      name: "", sku: "", barcode: "", category: "Raw Materials",
      quantity: 0, unit: "pcs", location: "",
      location_id: defaultLocationId || "", warehouse: "",
      supplier: "", supplier_id: "", min_stock: 0, reorder_qty: 0, unit_cost: 0, notes: "", image_base64: null,
    });
  }, [initial, open, defaultLocationId]);

  const isRawMaterial = (form.category || "").trim().toLowerCase() === "raw materials";

  const createSupplierInline = async () => {
    if (!newSupplier.name.trim()) { toast.error("Supplier name required"); return; }
    setCreatingSup(true);
    try {
      const payload = { ...newSupplier };
      if (!payload.email) delete payload.email;
      const { data } = await api.post("/suppliers", payload);
      toast.success(`Supplier "${data.name}" created`);
      setSuppliers((p) => [...p, data]);
      setForm((f) => ({ ...f, supplier_id: data.id, supplier: data.name }));
      setSupplierMode("select");
      setNewSupplier({ name: "", email: "", phone: "", contact_person: "" });
    } catch (e) { toast.error(extractErr(e, "Failed to create supplier")); }
    setCreatingSup(false);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 700 * 1024) { toast.error("Image must be <700 KB"); return; }
    const r = new FileReader();
    r.onload = () => update("image_base64", r.result);
    r.readAsDataURL(f);
  };
  const submit = async (e) => {
    e.preventDefault();
    // Client-side guard: Raw Materials must have a supplier
    if (isRawMaterial && !form.supplier_id && !(form.supplier && form.supplier.trim())) {
      toast.error("Raw Materials must have a supplier — please select or add one");
      return;
    }
    setSubmitting(true);
    const payload = { ...form, quantity: Number(form.quantity), min_stock: Number(form.min_stock), reorder_qty: Number(form.reorder_qty), unit_cost: Number(form.unit_cost) };
    if (!payload.location_id) payload.location_id = null;
    if (!payload.supplier_id) payload.supplier_id = null;
    await onSave(payload);
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit item" : "Add inventory item"}</DialogTitle>
          <DialogDescription>{initial ? "Update product details and on-hand quantities." : "Create a new SKU. You can change details any time."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2 flex items-center gap-4">
            {form.image_base64 ? (
              <div className="relative">
                <img src={form.image_base64} alt="preview" className="w-16 h-16 rounded-lg object-cover border border-border bg-white" />
                <button type="button" className="absolute -top-1 -right-1 rounded-full bg-background border border-border p-1" onClick={() => update("image_base64", null)}><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center text-muted-foreground"><ImageIcon className="w-5 h-5" /></div>
            )}
            <label className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm cursor-pointer hover:bg-accent/50">
              <Upload className="w-4 h-4" /> Upload image
              <input type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="inventory-image-input" />
            </label>
          </div>

          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="inventory-form-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>SKU *</Label>
            <Input required value={form.sku} onChange={(e) => update("sku", e.target.value)} className="font-mono" data-testid="inventory-form-sku-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Barcode / QR</Label>
            <Input value={form.barcode} onChange={(e) => update("barcode", e.target.value)} className="font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => update("category", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Quantity</Label>
            <Input type="number" step="any" value={form.quantity} onChange={(e) => update("quantity", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Unit</Label>
            <Input value={form.unit} onChange={(e) => update("unit", e.target.value)} placeholder="pcs, kg, L, box…" />
          </div>

          {/* Low-level threshold callout — emphasized */}
          <div className="md:col-span-2 rounded-xl border border-amber-500/30 bg-amber-500/[0.04] p-3" data-testid="inventory-form-lowlevel-block">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                <Bell className="w-3.5 h-3.5" />
              </div>
              <div>
                <div className="font-medium text-sm">Low-level threshold (alert when stock drops to or below)</div>
                <div className="text-[11px] text-muted-foreground">When stock hits this number, the workspace owner gets an automatic email suggesting a reorder from the supplier. You can edit this any time.</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Min stock *</Label>
                <Input
                  type="number" step="any" min="0"
                  value={form.min_stock}
                  onChange={(e) => update("min_stock", e.target.value)}
                  data-testid="inventory-form-min-stock"
                  placeholder="e.g. 50"
                  required={isRawMaterial}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Reorder qty (suggested order amount)</Label>
                <Input type="number" step="any" min="0" value={form.reorder_qty} onChange={(e) => update("reorder_qty", e.target.value)} placeholder="e.g. 200" />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Unit cost (€)</Label>
            <Input type="number" step="0.01" value={form.unit_cost} onChange={(e) => update("unit_cost", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Warehouse</Label>
            <Input value={form.warehouse} onChange={(e) => update("warehouse", e.target.value)} placeholder="Main" />
          </div>
          {locations.length > 1 && (
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Select value={form.location_id || "auto"} onValueChange={(v) => update("location_id", v === "auto" ? "" : v)}>
                <SelectTrigger data-testid="inventory-form-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Default location</SelectItem>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Location (free text)</Label>
            <Input value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="Shelf A-3" />
          </div>
          {/* Supplier — dropdown from /api/suppliers + inline create */}
          <div className="md:col-span-2 rounded-xl border border-primary/20 bg-primary/[0.03] p-3" data-testid="inventory-form-supplier-block">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Truck className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-sm">
                  Supplier {isRawMaterial && <span className="text-destructive">*</span>}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {isRawMaterial
                    ? "Required for Raw Materials — used in low-stock reorder alerts so we know who to contact."
                    : "Optional. Used in low-stock reorder emails when stock runs low."}
                </div>
              </div>
            </div>

            {supplierMode === "select" ? (
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                <Select
                  value={form.supplier_id || (form.supplier ? "_text" : "_none")}
                  onValueChange={(v) => {
                    if (v === "_none") {
                      setForm((f) => ({ ...f, supplier_id: "", supplier: "" }));
                    } else if (v === "_text") {
                      // keep free-text mode (no-op)
                    } else {
                      const sup = suppliers.find((s) => s.id === v);
                      setForm((f) => ({ ...f, supplier_id: v, supplier: sup?.name || "" }));
                    }
                  }}
                >
                  <SelectTrigger data-testid="inventory-form-supplier-select">
                    <SelectValue placeholder="Select a supplier…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— None —</SelectItem>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}{s.email ? ` · ${s.email}` : ""}
                      </SelectItem>
                    ))}
                    {!form.supplier_id && form.supplier && (
                      <SelectItem value="_text">Use free text: {form.supplier}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="sm" onClick={() => setSupplierMode("new")} data-testid="inventory-form-supplier-new-button">
                  <Plus className="w-3.5 h-3.5 mr-1" />New supplier
                </Button>
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-background p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium">Add new supplier</div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setSupplierMode("select")}><X className="w-3.5 h-3.5" /></Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-[11px]">Supplier name *</Label>
                    <Input value={newSupplier.name} onChange={(e) => setNewSupplier((p) => ({ ...p, name: e.target.value }))} placeholder="ACME Feeds Inc." data-testid="inventory-form-supplier-new-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px]">Contact person</Label>
                    <Input value={newSupplier.contact_person} onChange={(e) => setNewSupplier((p) => ({ ...p, contact_person: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px]">Email</Label>
                    <Input type="email" value={newSupplier.email} onChange={(e) => setNewSupplier((p) => ({ ...p, email: e.target.value }))} placeholder="sales@acme.example.com" data-testid="inventory-form-supplier-new-email" />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-[11px]">Phone</Label>
                    <Input value={newSupplier.phone} onChange={(e) => setNewSupplier((p) => ({ ...p, phone: e.target.value }))} />
                  </div>
                </div>
                <Button type="button" size="sm" onClick={createSupplierInline} disabled={creatingSup} className="w-full" data-testid="inventory-form-supplier-new-submit">
                  {creatingSup ? "Creating…" : "Save supplier"}
                </Button>
              </div>
            )}
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} />
          </div>
          <DialogFooter className="md:col-span-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="inventory-form-cancel-button">Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="inventory-form-submit-button">{submitting ? "Saving…" : initial ? "Save changes" : "Create item"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HistoryModal({ item, onClose }) {
  const [history, setHistory] = useState(null);
  const [tab, setTab] = useState("lots");

  useEffect(() => {
    api.get(`/inventory/${item.id}/lot-history`)
      .then(({ data }) => setHistory(data))
      .catch(() => setHistory({ lots: [], movements: [], summary: {} }));
  }, [item.id]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="inventory-history-modal">
        <DialogHeader>
          <DialogTitle>History — {item.name}</DialogTitle>
          <DialogDescription className="font-mono text-xs">{item.sku}</DialogDescription>
        </DialogHeader>

        {!history ? (
          <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : (
          <>
            {/* Summary chips */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
              <SummaryChip label="Lots" value={history.summary?.total_lots || 0} />
              <SummaryChip label="Active" value={history.summary?.active_lots || 0} />
              <SummaryChip label="Intakes" value={history.summary?.total_intakes || 0} />
              <SummaryChip label="Outtakes" value={history.summary?.total_outtakes || 0} />
              <SummaryChip label="To Tanks" value={history.summary?.total_tank_consumptions || 0} />
              <SummaryChip label="To Orders" value={history.summary?.total_production_consumptions || 0} />
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 mb-3 border-b border-border">
              <button
                type="button"
                className={`px-3 py-1.5 text-sm font-medium border-b-2 ${tab === "lots" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                onClick={() => setTab("lots")}
                data-testid="history-tab-lots"
              >
                Lots ({(history.lots || []).length})
              </button>
              <button
                type="button"
                className={`px-3 py-1.5 text-sm font-medium border-b-2 ${tab === "movements" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                onClick={() => setTab("movements")}
                data-testid="history-tab-movements"
              >
                Movements ({(history.movements || []).length})
              </button>
            </div>

            {tab === "lots" ? (
              (history.lots || []).length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No lots tracked yet for this item. Use a Lot # on Goods Intake to enable traceability.
                </div>
              ) : (
                <div className="space-y-2">
                  {history.lots.map((lot) => (
                    <div key={lot.id} className="rounded-lg border border-border bg-card p-3" data-testid={`history-lot-${lot.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-mono text-sm font-semibold truncate">{lot.lot_number}</div>
                          <div className="text-[11px] text-muted-foreground">
                            Intake {lot.intake_date ? new Date(lot.intake_date).toLocaleDateString() : "—"}
                            {lot.supplier ? ` · ${lot.supplier}` : ""}
                            {lot.expiry_date ? ` · exp ${new Date(lot.expiry_date).toLocaleDateString()}` : ""}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[11px] text-muted-foreground">Remaining / Intake</div>
                          <div className="font-mono text-sm">
                            <span className={Number(lot.quantity || 0) > 0 ? "text-foreground" : "text-muted-foreground"}>{lot.quantity}</span>
                            <span className="text-muted-foreground"> / {lot.intake_qty || lot.quantity}</span>
                          </div>
                        </div>
                      </div>
                      {(lot.consumption || []).length > 0 && (
                        <div className="mt-2 rounded border border-border bg-muted/20 p-2">
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Where consumed</div>
                          <div className="space-y-0.5">
                            {lot.consumption.map((c, i) => (
                              <div key={i} className="text-[11px] flex items-center justify-between gap-2">
                                <span className="truncate">
                                  <span className="inline-flex rounded-full bg-muted px-1.5 py-0.5 text-[10px] capitalize mr-1.5">{(c.type || "").replace(/_/g, " ")}</span>
                                  {c.tank_batch_number && <span className="font-mono">{c.tank_batch_number}</span>}
                                  {c.tank_name && <span className="ml-1">{c.tank_name}</span>}
                                  {c.order_number && <span className="font-mono">{c.order_number}</span>}
                                  {c.order_customer && <span className="ml-1">· {c.order_customer}</span>}
                                </span>
                                <span className="font-mono tabular-nums text-muted-foreground">{c.quantity}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            ) : (
              (history.movements || []).length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">No movements yet for this item.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                      <th className="py-2">When</th><th>Type</th><th className="text-right">Qty</th><th>Lot</th><th>User</th><th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.movements.map((m) => (
                      <tr key={m.id} className="border-b border-border last:border-0">
                        <td className="py-2 whitespace-nowrap text-xs">{new Date(m.timestamp).toLocaleString()}</td>
                        <td><span className="inline-flex rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[11px] capitalize">{m.type.replace(/_/g, " ")}</span></td>
                        <td className="text-right tabular-nums font-mono">{m.quantity}</td>
                        <td className="font-mono text-xs">{m.lot_number || "—"}</td>
                        <td className="text-muted-foreground text-xs">{m.user_email}</td>
                        <td className="text-muted-foreground truncate max-w-[180px] text-xs">{m.note || m.reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryChip({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-2 py-1.5 text-center">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-mono text-sm font-semibold">{value}</div>
    </div>
  );
}
